import React, { useEffect } from "react";
import { Card, Button, Dialog, DialogActions, DialogContent, DialogTitle, FlexGrid, TextField, Typography, useThemeProvider, DefaultComponentProps } from '@albo-ui/core';
import { ChangeAlertCallbackType, ChangeLoaderCallbackType } from "../../Root";
import { useChangeStatusLead } from "./hooks/useChangeStatusLead";
import { LeadDataType } from "../../customwidgets/leads-tracking-report/types";
import { useConfirm } from "../confirm";
import RadioButton from "../../elements/RadioButton";
import { MenuItem, Select, Divider, Box } from '@mui/material';
import { TextField as TextMaterialField } from "@mui/material";
import { FormGroup, FormControlLabel, Switch } from "@mui/material";
import { isContractSentStatus } from "../../customwidgets/leads-onboarding-sections/pldStatus";


export type ChangeStatusLeadDialogProps = {
    open: boolean;
    lead: LeadDataType
    onConfirm: () => void;
    onClose: () => void;
    changeAlert: ChangeAlertCallbackType;
    changeLoader: ChangeLoaderCallbackType;
    catalog?: any;
    config?: any;
    pldStatus?: string;
} & DefaultComponentProps;

export const ChangeStatusLead = ({ open, lead, className, style, catalog, config, onConfirm, onClose, changeAlert, changeLoader, pldStatus }: ChangeStatusLeadDialogProps) => {

    const { status, statusSelected, customerUuidProps, isInvalidStatus, isDone, loading, selectOccupation, editField, payloadCreate, fee, isForeigner, onHandleChange, onSelectStatus, canNextStatus, onConfirmChangeStatus, hasAction, setIsForeigner } = useChangeStatusLead(onConfirm, onClose, open, changeAlert, lead, config)
    const { theme } = useThemeProvider();
    const { DialogConfirm, confirm: confirmUpdateStatus } = useConfirm(
        'Cambiar estatus',
        fee ? `Estás a punto de enviar un contrato ${fee["metadata"] ? `con el ${fee["metadata"]["percentage_fee"]}% de cobro de fee` : "sin cobro de fee indicado" }` : '¿Estás seguro que deseas realizar este cambio?' 
    )
    const handleConfirm = async () => {
        const isConfirm = await confirmUpdateStatus()
        if (isConfirm) {
            onConfirmChangeStatus()
        } else {
            onClose()
        }
    };

    const resolvedPldStatus = pldStatus || lead?.pld_status;
    const isPldBlocked = resolvedPldStatus
        ? resolvedPldStatus !== "approved"
        : !lead?.pld_approved;
    const pldBlockReasonByStatus: Record<string, string> = {
        incomplete_error: 'El cruce contra listas restringidas y blacklist interna no se completó. "Contrato enviado" permanece bloqueado hasta que el cruce termine sin coincidencias.',
        manual_review: 'Coincidencia inexacta en listas restringidas. Manual Check debe hacer el cruce manual y registrar si el cruce queda aprobado o no aprobado.',
        match_confirmed: 'Coincidencia exacta confirmada en listas restringidas. La cuenta no debe aperturarse: "Contrato enviado" está bloqueado.',
        manual_rejected: 'El cruce manual quedó NO aprobado. La cuenta no puede abrirse; "Contrato enviado" permanece bloqueado.'
    };
    const pldBlockReason = pldBlockReasonByStatus[resolvedPldStatus] || pldBlockReasonByStatus.manual_review;

    useEffect(() => {
        changeLoader(loading)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading])

    if (status.length === 0 || loading) {
        //TODO mostrar loader
        console.log("Cargando estatus...")
        return <></>
    }

    return (
        <>
            <DialogConfirm />
            <Dialog className={className} style={style} onClose={onClose} open={open}>
                <DialogTitle>
                    <Typography variant="h2">Cambiar Estatus</Typography>
                </DialogTitle>
                <DialogContent>
                    <FlexGrid container fullHeight fullWidth direction="column" justify='space-between' alignItems='flex-start' spacing={2}>
                        <Card fullHeight fullWidth alignItems='flex-start' style={{ borderColor: theme.alertTypesColor.warning }} padding={2}>
                            <FlexGrid fullWidth direction="row" justify='space-between' alignItems='space-between' container spacing={2}>
                                <Typography variant="h2" style={{ color: theme.alertTypesColor.warning }}>
                                    <i className="ibon-info"></i>
                                </Typography>
                                <FlexGrid container fullHeight fullWidth direction="column" justify='space-between' alignItems='flex-start' spacing={1}>
                                    <Typography variant="smallTitle" style={{ color: theme.alertTypesColor.warning }}>
                                        Recuerda
                                    </Typography>
                                    <Typography variant="paragraph">
                                        Al darle click en "Cambiar estatus" el cliente verá el cambio reflejado en plataforma.
                                    </Typography>
                                </FlexGrid>
                            </FlexGrid>
                        </Card>

                        {/* Alerta de bloqueo por cruce no aprobado (dinámica según pld_status, alineada al prototipo) */}
                        {isPldBlocked && (
                            <Card fullHeight fullWidth alignItems='flex-start' style={{ borderColor: '#E74C3C', backgroundColor: '#FFF5F5', marginTop: 10 }} padding={2}>
                                <FlexGrid fullWidth direction='row' justify='space-between' alignItems='flex-start' container spacing={2}>
                                    <Typography variant="h2" style={{color: theme.alertTypesColor.error}}>
                                        <i className="ibon-exclamation"></i>
                                    </Typography>
                                    <FlexGrid container fullHeight fullWidth direction='column' justify='space-between' alignItems='flex-start' spacing={1}>
                                        <Typography variant="smallTitle" style={{ color: theme.alertTypesColor.error }}>
                                            Cruce en listas restringidas no aprobado
                                        </Typography>
                                        <Typography variant="paragraph">
                                            {pldBlockReason}
                                        </Typography>
                                    </FlexGrid>
                                </FlexGrid>
                            </Card>
                        )}

                        <Typography variant='smallTitle' style={{ paddingTop: theme.spaces[1] }}>
                            Estatus actual
                        </Typography>

                        <Card fullHeight fullWidth alignItems='flex-start' flexDirection="column" padding={2}>
                            <FlexGrid container fullHeight fullWidth direction="column" justify='space-between' alignItems='flex-start' spacing={0}>
                                {status.map(s => {
                                    const isContractSent = isContractSentStatus(s);
                                    const isBlockedByPLD = isContractSent && isPldBlocked && !hasAction("allowChangeStatus");
                                    const isDisabled = !canNextStatus(s.name) || isBlockedByPLD;

                                    return (
                                        <FlexGrid fullWidth direction="row" justify='flex-start' alignItems='center' container spacing={3} key={s.value} style={{ marginBottom: 6 }}>
                                            <RadioButton
                                                name={"status"}
                                                value={s.name}
                                                checked={s.name === statusSelected}
                                                onChange={onSelectStatus}
                                                label={s.metadata.description}
                                                disabled={isDisabled}
                                            />
                                            {isBlockedByPLD && (
                                                <Box 
                                                    component="span" 
                                                    sx={{ 
                                                        display: "inline-flex", 
                                                        alignItems: "center", 
                                                        gap: 0.5, 
                                                        px: 1.2, 
                                                        py: 0.3, 
                                                        borderRadius: 10, 
                                                        border: "1px solid #FFA8A8", 
                                                        backgroundColor: "#FFF5F5", 
                                                        color: "#C92A2A", 
                                                        fontSize: "0.75rem", 
                                                        fontWeight: 600,
                                                        ml: 1
                                                    }}
                                                >
                                                    🔒 requiere cruce aprobado
                                                </Box>
                                            )}
                                        </FlexGrid>
                                    );
                                })}
                            </FlexGrid>
                            {isDone && !hasAction("autoClabe") ? 
                                <FlexGrid container fullHeight fullWidth direction="column" justify='space-between' alignItems='flex-start' style={{ paddingTop: theme.spaces[3] }} spacing={1}>
                                    <label>{hasAction("afbCreate") && lead.type === "PM" ? "CLABE" : "UUID"} del cliente</label>
                                    <TextField
                                        {...customerUuidProps}
                                        placeholder={""}
                                    />
                                </FlexGrid>
                            :
                                isDone && hasAction("autoClabe") && lead.type !== "PM" &&
                                <FlexGrid container fullHeight fullWidth direction="column" justify='space-between' alignItems='flex-start' style={{ paddingTop: theme.spaces[3] }} spacing={1}>
                                    <label>{hasAction("afbCreate") && "UUID"} del cliente</label>
                                    <TextField
                                        {...customerUuidProps}
                                        placeholder={""}
                                    />
                                </FlexGrid>
                            }
                            {selectOccupation && payloadCreate &&
                                <FlexGrid style={{display: "contents"}}>
                                    <Typography variant="paragraph">
                                        Selecciona la Actividad Económica del Cliente, según lo indicado en su Onboarding:
                                    </Typography>
                                    <Typography variant="smallTitle">
                                       {selectOccupation}
                                    </Typography>
                                    <Select MenuProps={{style: {zIndex: 35001}}} sx={{mt: 2}}
                                        value={Number(payloadCreate['general']['occupation']) ? payloadCreate['general']['occupation'] : "-1"} onChange={onHandleChange.bind(this, 'general', 'occupation')} size='small' color='secondary' fullWidth>
                                            <MenuItem value={'-1'} disabled>Seleccione</MenuItem>
                                            { catalog?.map((item, index) => {
                                                return <MenuItem value={item.k} key={`occupation-${index}`}>{item.v}</MenuItem>
                                            })}
                                    </Select>
                                </FlexGrid>
                            }
                            {editField && payloadCreate &&
                                <FlexGrid style={{display: "contents"}}>
                                    <Typography variant="paragraph" style={{marginBlock: 20}}>
                                        El dato del Lead ya se encuentra en la Base de Datos, por favor corrige para continuar.
                                    </Typography>
                                    <TextMaterialField label={editField["label"]} value={payloadCreate["general"][editField["key"]]}
                                        onChange={onHandleChange.bind(this, 'general', editField["key"])} size='small' color='secondary' fullWidth
                                    />
                                </FlexGrid>
                            }
                            { statusSelected === "done" && <Box sx={{width: "100%"}}>
                                <Divider sx={{my: 2, width: "100%"}}/>
                                <FormGroup>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={isForeigner}
                                                onChange={(e) => setIsForeigner(isForeigner ? false : true)}
                                            />
                                        }
                                        label={"Lead con domicilio en el Extranjero"}
                                    />
                                </FormGroup>
                            </Box>}
                        </Card>
                    </FlexGrid>
                </DialogContent>
                <DialogActions>
                    <FlexGrid container fullHeight fullWidth justify='space-between' alignItems='flex-end' style={{ marginTop: '20px' }}>
                        <FlexGrid item>
                            <Button onClick={onClose} buttonType='secondary' size='normal'>
                                Cancelar
                            </Button>
                        </FlexGrid>
                        <FlexGrid item>
                            <Button disabled={isInvalidStatus} onClick={handleConfirm} size='normal'>
                                Cambiar estatus
                            </Button>
                        </FlexGrid>
                    </FlexGrid>
                </DialogActions>
            </Dialog>
        </>
    );
};
